import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { useNavigate } from "react-router-dom";
import { PAYMENT_MESSAGES } from "../../../modules/common/shared/messages";
import { useStudentFeeDues } from "../hooks/useStudentFeeDues";


export const StudentFeeDues = () => {
  const navigate = useNavigate();

  const { feeDues, loading, error, paymentInProgress, paymentLink, activePoStatus, aggregatedFeeDue } = useStudentFeeDues();
  const isPaymentPending = activePoStatus === "PENDING_VBV";

  return (
    <Card elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 2 }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        {/* the page title ("Outstanding") comes from the page's top bar */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {paymentInProgress && isPaymentPending ? (
              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" fontWeight={600}>
                  Your previous transaction is pending
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Please wait while it is being processed. You can check the completion status in the{" "}
                  <a href="/app/fees/payments">Payments</a> page.
                </Typography>
              </Alert>
            ) : paymentInProgress ? (
              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" fontWeight={600}>
                  {PAYMENT_MESSAGES.PLEASE_COMPLETE_PAYMENT}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  If you cancelled the payment, you can either complete it now or wait 15 minutes for it to
                  expire. To proceed with the payment, please click <a href={paymentLink}>here</a>.
                </Typography>
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                Outstanding fee dues by grade, section and term
              </Typography>
            )}

            {feeDues.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  py: 4,
                  border: "1px solid #eceff2",
                  borderRadius: 1.5,
                }}
              >
                <CheckCircleOutlineRoundedIcon color="success" />
                <Typography variant="body2" color="text.secondary">
                  No pending fee dues
                </Typography>
              </Box>
            ) : (
              <>
                {/* One row per due: grade/section and term on the left, amount on the right —
                    no columns, so it fits any width without horizontal scroll. */}
                <Box sx={{ border: "1px solid #eceff2", borderRadius: 1.5, overflow: "hidden" }}>
                  {feeDues.map((fd, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid #eceff2",
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: "#0f172a" }}>
                          {fd.gradeName} · Section {fd.sectionName}
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }}>Term {fd.terms}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap" }}>
                        &#8377;{fd.payable.toLocaleString("en-IN")}
                      </Typography>
                    </Box>
                  ))}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      px: 2,
                      py: 1.5,
                      bgcolor: "#eff6ff",
                    }}
                  >
                    <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: "#0f172a" }}>Total payable</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: "primary.dark", whiteSpace: "nowrap" }}>
                      &#8377;{(aggregatedFeeDue?.payable ?? 0).toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/app/fees/pay", { state: { feeDue: aggregatedFeeDue } })}
                    disabled={paymentInProgress}
                    sx={{ width: { xs: "100%", sm: 240 }, py: 1.1, fontWeight: 600, borderRadius: 2, textTransform: "none", fontSize: 15 }}
                  >
                    Pay &#8377;{(aggregatedFeeDue?.payable ?? 0).toLocaleString("en-IN")}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
